import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import requestStore from '../../stores/requestStore'
import { boardDeletionMappingsComplete, buildBoardDeletionMappings, requestCountLabel, terminalMigrationTargetLabel } from '../../utils/requestLifecycleUi'

export default function BoardDeletionDialog({ open, board, initialPlan, onClose, onDeleted }) {
  const [plan, setPlan] = useState(initialPlan)
  const [destinations, setDestinations] = useState({})
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !board) return
    setPlan(initialPlan || null)
    setDestinations({})
    setError('')
    if (!initialPlan) requestStore.getBoardDeletionPlan(board.boardId).then(setPlan).catch(loadError => setError(loadError.response?.data?.error || 'Не удалось построить план удаления'))
  }, [open, board, initialPlan])

  const nonempty = useMemo(() => plan?.columns?.filter(column => column.total > 0) || [], [plan])
  const mappings = useMemo(() => buildBoardDeletionMappings(nonempty, destinations), [nonempty, destinations])
  const complete = boardDeletionMappingsComplete(mappings)

  const chooseBoard = (column, targetBoardId) => {
    setDestinations(current => ({ ...current, [column.id]: { boardId: targetBoardId, columnId: null } }))
  }

  const submit = async () => {
    if (!complete) return
    setSubmitting(true)
    setError('')
    try {
      await requestStore.migrateAndDeleteBoard(board.boardId, mappings)
      onDeleted()
    } catch (migrationError) {
      setError(migrationError.response?.data?.error || 'План устарел или перенос не выполнен')
      try {
        setPlan(await requestStore.getBoardDeletionPlan(board.boardId))
        setDestinations({})
      } catch { /* keep the original error */ }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Перенос заявок перед удалением доски</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!plan && <Typography>Загрузка…</Typography>}
        {plan && (
          <>
            <Typography sx={{ mb: 2 }}>На доске «{plan.board.name}»: {plan.total} заявок. Перенос и удаление будут выполнены одной операцией.</Typography>
            <Stack spacing={2}>
              {nonempty.map(column => {
                const destination = destinations[column.id] || {}
                const targetBoard = plan.targetBoards.find(candidate => candidate.boardId === destination.boardId)
                const normalTargets = targetBoard?.columns.filter(candidate => candidate.systemType === 'NORMAL') || []
                return (
                  <Box key={column.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2">{column.name} — {requestCountLabel(column.total, column.archived)}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Целевая доска</InputLabel>
                        <Select label="Целевая доска" value={destination.boardId || ''} onChange={event => chooseBoard(column, event.target.value)}>
                          {plan.targetBoards.map(candidate => <MenuItem key={candidate.boardId} value={candidate.boardId}>{candidate.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      {column.systemType === 'NORMAL' ? (
                        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!targetBoard}>
                          <InputLabel>Целевая колонка</InputLabel>
                          <Select label="Целевая колонка" value={destination.columnId || ''} onChange={event => setDestinations(current => ({ ...current, [column.id]: { ...destination, columnId: event.target.value } }))}>
                            {normalTargets.map(candidate => <MenuItem key={candidate.columnId} value={candidate.columnId}>{candidate.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ) : <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>{terminalMigrationTargetLabel(column.systemType, targetBoard)}</Typography>}
                    </Box>
                  </Box>
                )
              })}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>Отмена</Button>
        <Button color="error" variant="contained" disabled={!complete || submitting} onClick={submit}>Перенести и удалить</Button>
      </DialogActions>
    </Dialog>
  )
}
